import { useRef, useState } from "react";
import { Upload, Mic, Square, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, assetUrl } from "@/lib/api";

export const AudioUpload = ({ value, onChange }) => {
  const inputRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/audio", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Audio saved");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Audio must be under 15 MB"); e.target.value = ""; return; }
    await upload(file);
    e.target.value = "";
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        await upload(new File([blob], "recording.webm", { type: blob.type }));
      };
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
    } catch (err) {
      toast.error("Microphone access denied or unavailable");
    }
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  return (
    <div className="space-y-3" data-testid="audio-upload">
      {value && (
        <div className="flex items-center gap-2">
          <audio controls src={assetUrl(value)} className="w-full h-10" data-testid="audio-upload-preview" />
          <button type="button" data-testid="audio-upload-remove" onClick={() => onChange("")}
            className="text-muted-foreground hover:text-destructive shrink-0"><X size={15} /></button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <input ref={inputRef} type="file" accept="audio/*" className="hidden" onChange={pick} data-testid="audio-upload-input" />
        <button type="button" data-testid="audio-upload-btn" disabled={busy || recording} onClick={() => inputRef.current?.click()}
          className="rounded-full border border-border px-4 py-2 text-xs text-foreground hover:border-primary/50 transition-colors flex items-center gap-2">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          {busy ? "Uploading…" : "Upload audio"}
        </button>
        {recording ? (
          <button type="button" data-testid="audio-record-stop" onClick={stopRecording}
            className="rounded-full border border-border bg-muted px-4 py-2 text-xs text-muted-foreground flex items-center gap-2">
            <Square size={12} /> Stop recording
          </button>
        ) : (
          <button type="button" data-testid="audio-record-btn" disabled={busy} onClick={startRecording}
            className="rounded-full border border-border px-4 py-2 text-xs text-foreground hover:border-primary/50 transition-colors flex items-center gap-2">
            <Mic size={13} /> Record
          </button>
        )}
        <span className="text-xs text-muted-foreground">MP3, WAV, M4A · max 15 MB</span>
      </div>
    </div>
  );
};
